import React, { useState, useEffect } from 'react';
import { useParams, useNavigate, useSearchParams } from 'react-router-dom';
import { shareProfileLink } from '../../utils/shareLink';
import {
  Box,
  Container,
  Card,
  CardContent,
  Typography,
  Button,
  Avatar,
  Chip,
  Rating,
  Grid,
  Paper,
  Tabs,
  Tab,
  List,
  ListItem,
  ListItemText,
  CircularProgress,
  Alert,
  LinearProgress,
  Divider,
  IconButton,
  Collapse,
  Dialog,
  DialogTitle,
  DialogContent
} from '@mui/material';
import {
  Verified,
  LocationOn,
  Message,
  Work,
  Schedule,
  Star,
  TrendingUp,
  ExpandMore,
  ExpandLess,
  CheckCircle,
  Close,
  ContentCopy
} from '@mui/icons-material';
import api from '../../services/api';
import messagingService from '../../services/messagingService';
import AIContractorHiringDialog from './AIContractorHiringDialog';
import { brand } from '../../theme/colors';
import useSavedContractors from '../../hooks/useSavedContractors';
import Seo from '../Seo';
import TrustLine from './TrustLine';

/**
 * Enhanced Contractor Profile Page
 * Cleaner layout with clear hierarchy and Trust Score breakdown
 */
const normalizeStringList = (value) => {
  if (Array.isArray(value)) return value.filter(Boolean);
  if (typeof value === 'string') {
    try {
      const parsed = JSON.parse(value);
      if (Array.isArray(parsed)) return parsed.filter(Boolean);
    } catch {
      // Keep treating ordinary comma-separated text as text.
    }
    return value.split(',').map(item => item.trim()).filter(Boolean);
  }
  return [];
};

const normalizeImageList = (value) => {
  if (Array.isArray(value)) return value.filter(Boolean);
  if (typeof value === 'string') {
    try {
      const parsed = JSON.parse(value);
      if (Array.isArray(parsed)) return parsed.filter(Boolean);
    } catch {
      // Keep treating ordinary string URLs as a single image.
    }
    return value ? [value] : [];
  }
  return [];
};

const EnhancedContractorProfile = () => {
  const { contractorId } = useParams();
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const savedHook = useSavedContractors();

  const tabMap = { about: 0, portfolio: 1, reviews: 2 };
  const initialTab = tabMap[searchParams.get('tab')] ?? 0;
  const isPreview = searchParams.get('preview') === '1' || searchParams.get('preview') === 'true';

  const [contractor, setContractor] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [activeTab, setActiveTab] = useState(initialTab);
  const [quoteDialogOpen, setQuoteDialogOpen] = useState(false);
  const [trustScoreExpanded, setTrustScoreExpanded] = useState(false);
  const [portfolioItems, setPortfolioItems] = useState([]);
  const [selectedPortfolioItem, setSelectedPortfolioItem] = useState(null);
  const [shareCopied, setShareCopied] = useState(false);
  const [reviews, setReviews] = useState([]);
  const [userProjects, setUserProjects] = useState([]);

  useEffect(() => {
    loadContractorProfile();
    loadUserProjects();
  }, [contractorId]);

  const loadContractorProfile = async () => {
    setLoading(true);
    setError(null);

    try {
      const response = await api.get(`/contractors/${contractorId}`);
      
      if (response.data.success) {
        const data = response.data.data;
        setContractor(data);
        
        // Load portfolio
        loadPortfolio();
        
        // Load reviews
        loadReviews();
      } else {
        setError('Failed to load contractor profile');
      }
    } catch (err) {
      console.error('Error loading contractor:', err);
      setError(err.response?.data?.message || 'Failed to load contractor profile');
    } finally {
      setLoading(false);
    }
  };

  const loadPortfolio = async () => {
    try {
      const response = await api.get(`/contractors/${contractorId}/portfolio`);
      const result = response.data;
      if (result.success) {
        setPortfolioItems(result.data || []);
      }
    } catch (err) {
      console.error('Failed to load portfolio:', err);
    }
  };

  const loadReviews = async () => {
    try {
      const response = await api.get(`/reviews/user/${contractorId}?limit=10`);
      const result = response.data;
      if (result.success) {
        const reviewsData = Array.isArray(result.data)
          ? result.data
          : (result.data?.reviews || []);
        setReviews(reviewsData);
      }
    } catch (err) {
      console.error('Failed to load reviews:', err);
    }
  };

  const loadUserProjects = async () => {
    try {
      const response = await api.get('/projects');
      const data = response.data;
      if (data.success && data.data && Array.isArray(data.data.projects)) {
        setUserProjects(data.data.projects);
      }
    } catch (error) {
      console.error('Failed to load projects:', error);
    }
  };

  const handleSendMessage = async () => {
    try {
      const res = await messagingService.getOrCreateConversation(contractorId);
      const convId = res.data?.id;
      navigate(convId ? `/messages?conversation=${convId}` : '/messages');
    } catch {
      navigate('/messages');
    }
  };

  const handleRequestQuote = () => {
    setQuoteDialogOpen(true);
  };

  const handleShareProfile = async () => {
    // /share/ URLs carry server-rendered OG tags for WhatsApp link previews.
    const result = await shareProfileLink({
      path: `/share/contractor/${contractorId}`,
      title: `${contractor?.business_name || 'Contractor'} on Eqaya`
    });
    if (result === 'copied') {
      setShareCopied(true);
      window.setTimeout(() => setShareCopied(false), 2500);
    }
  };

  const selectedPortfolioImages = selectedPortfolioItem
    ? normalizeImageList(selectedPortfolioItem.after_images)
    : [];

  if (loading) {
    return (
      <Container maxWidth="lg" sx={{ py: 4, textAlign: 'center' }}>
        <CircularProgress />
        <Typography variant="body1" color="text.secondary" sx={{ mt: 2 }}>
          Loading contractor profile...
        </Typography>
      </Container>
    );
  }

  if (error || !contractor) {
    return (
      <Container maxWidth="lg" sx={{ py: 4 }}>
        <Alert severity="error">
          {error || 'Contractor not found'}
        </Alert>
        <Button onClick={() => navigate('/contractors')} sx={{ mt: 2 }}>
          Back to Contractors
        </Button>
      </Container>
    );
  }

  // Extract data safely
  const name = contractor.business_name || `${contractor.first_name || ''} ${contractor.last_name || ''}`.trim() || 'Contractor';
  const verified = contractor.verified_documents > 0;
  const avatar = contractor.profile_picture || '';
  const bio = contractor.bio || contractor.tagline || 'Professional contractor with proven track record.';
  const location = contractor.location || 'Zimbabwe';
  // Contact info intentionally omitted — all communication through in-app messaging
  const rating = parseFloat(contractor.average_rating || 0);
  const reviewCount = parseInt(contractor.review_count || 0);
  const totalProjects = parseInt(contractor.total_projects || 0);
  const completedProjects = parseInt(contractor.completed_projects || 0);
  const onTimeRate = parseInt(contractor.on_time_rate || 0);
  const responseRate = parseInt(contractor.response_rate || 0);
  const trustScore = parseInt(contractor.trust_score || 0);
  const trustScoreProvisional = Boolean(contractor.trust_score_provisional);
  const trustScoreBreakdown = contractor.trust_score_breakdown || {};
  const specializations = normalizeStringList(contractor.specializations);
  const portfolioItemsWithPhotos = portfolioItems.filter(item => normalizeImageList(item.after_images).length > 0).length;
  const portfolioItemsWithValue = portfolioItems.filter(item => item.project_value).length;
  const previewChecklist = [
    {
      label: 'Add at least 3 portfolio projects',
      done: portfolioItems.length >= 3
    },
    {
      label: 'Include real completed-project photos',
      done: portfolioItemsWithPhotos >= Math.min(portfolioItems.length, 3) && portfolioItems.length > 0
    },
    {
      label: 'Add project values where appropriate',
      done: portfolioItemsWithValue > 0
    },
    {
      label: 'Earn client reviews to strengthen trust',
      done: reviewCount > 0
    }
  ];
  const hasPreviewSuggestions = previewChecklist.some(item => !item.done);

  return (
    <Container maxWidth="lg" sx={{ py: 4 }}>
      <Seo
        title={`${name} — Contractor in ${location}`}
        description={(bio || '').slice(0, 160)}
        canonicalPath={`/p/contractor/${contractorId}`}
      />
      {isPreview && (
        <Alert
          severity="info"
          sx={{ mb: 3, alignItems: 'center' }}
          action={
            <Box sx={{ display: 'flex', gap: 1, flexWrap: 'wrap', justifyContent: 'flex-end' }}>
              <Button color="inherit" size="small" startIcon={<ContentCopy />} onClick={handleShareProfile}>
                {shareCopied ? 'Copied' : 'Share Profile'}
              </Button>
              <Button color="inherit" size="small" onClick={() => navigate('/contractor/portfolio')}>
                Edit My Portfolio
              </Button>
            </Box>
          }
        >
          <Typography variant="subtitle2" sx={{ fontWeight: 700 }}>
            Previewing what project owners see
          </Typography>
          <Typography variant="body2">
            Portfolio projects, reviews, trust score, and contact actions help owners decide whether to request a quote.
          </Typography>
        </Alert>
      )}

      {isPreview && hasPreviewSuggestions && (
        <Card sx={{ mb: 3, border: '1px solid', borderColor: 'divider' }}>
          <CardContent>
            <Typography variant="subtitle1" sx={{ fontWeight: 700, mb: 1 }}>
              Strengthen your owner view
            </Typography>
            <Grid container spacing={1.5}>
              {previewChecklist.map((item) => (
                <Grid item xs={12} sm={6} key={item.label}>
                  <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                    <CheckCircle sx={{ fontSize: 18, color: item.done ? 'success.main' : 'text.disabled' }} />
                    <Typography variant="body2" color={item.done ? 'text.primary' : 'text.secondary'}>
                      {item.label}
                    </Typography>
                  </Box>
                </Grid>
              ))}
            </Grid>
          </CardContent>
        </Card>
      )}

      {/* Header Card */}
      <Card sx={{ mb: 3 }}>
        <Box
          sx={{
            height: 150,
            background: brand.gradient,
            position: 'relative'
          }}
        />
        
        <CardContent sx={{ position: 'relative', pt: 0 }}>
          <Box sx={{ display: 'flex', flexDirection: { xs: 'column', md: 'row' }, gap: 3, mt: -6 }}>
            {/* Avatar */}
            <Avatar
              src={avatar}
              alt={name}
              sx={{
                width: 120,
                height: 120,
                border: '4px solid white',
                boxShadow: 3
              }}
            >
              {name.charAt(0).toUpperCase()}
            </Avatar>

            {/* Name and Basic Info */}
            <Box sx={{ flexGrow: 1, pt: { xs: 0, md: 6 } }}>
              <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 1 }}>
                <Typography variant="h4" sx={{ fontWeight: 600 }}>
                  {name}
                </Typography>
                {verified && (
                  <Verified sx={{ fontSize: 28, color: 'primary.main' }} />
                )}
              </Box>

              <Box sx={{ display: 'flex', alignItems: 'center', gap: 2, mb: 2, flexWrap: 'wrap' }}>
                <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
                  <LocationOn sx={{ fontSize: 18, color: 'text.secondary' }} />
                  <Typography variant="body2" color="text.secondary">
                    {location}
                  </Typography>
                </Box>
                
                {specializations.length > 0 && (
                  <Box sx={{ display: 'flex', gap: 1, flexWrap: 'wrap' }}>
                    {specializations.slice(0, 3).map((spec, index) => (
                      <Chip key={index} label={spec} size="small" />
                    ))}
                  </Box>
                )}
              </Box>

              {/* Rating */}
              {reviewCount > 0 ? (
                <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                  <Rating value={rating} precision={0.1} readOnly />
                  <Typography variant="h6" sx={{ fontWeight: 600 }}>
                    {rating.toFixed(1)}
                  </Typography>
                  <Typography variant="body2" color="text.secondary">
                    ({reviewCount} {reviewCount === 1 ? 'review' : 'reviews'})
                  </Typography>
                </Box>
              ) : (
                <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                  <Rating value={0} readOnly />
                  <Typography variant="body2" color="text.secondary">
                    No reviews yet
                  </Typography>
                </Box>
              )}
              <TrustLine facts={contractor.trust_facts} sx={{ mt: 1.5 }} />
            </Box>

            {/* Action Buttons */}
            <Box sx={{ display: 'flex', flexDirection: 'column', gap: 1, pt: { xs: 0, md: 6 } }}>
              {isPreview ? (
                <>
                  <Button
                    variant="contained"
                    size="large"
                    startIcon={<Message />}
                    disabled
                    fullWidth
                  >
                    Send Message
                  </Button>
                  <Button
                    variant="contained"
                    size="large"
                    disabled
                    fullWidth
                    sx={{
                      background: brand.gradient,
                      '&.Mui-disabled': {
                        color: 'rgba(255,255,255,0.75)',
                        background: brand.gradient
                      }
                    }}
                  >
                    Request Quote
                  </Button>
                  <Typography variant="caption" color="text.secondary" sx={{ maxWidth: 260 }}>
                    Project owners can use these actions from your public profile.
                  </Typography>
                </>
              ) : (
                <>
                  <Button
                    variant="contained"
                    size="large"
                    startIcon={<Message />}
                    onClick={handleSendMessage}
                    fullWidth
                  >
                    Send Message
                  </Button>
                  <Button
                    variant="contained"
                    size="large"
                    onClick={handleRequestQuote}
                    fullWidth
                    sx={{
                      background: brand.gradient,
                      '&:hover': {
                        background: brand.gradientHover
                      }
                    }}
                  >
                    Request Quote
                  </Button>
                  <Button
                    variant="outlined"
                    size="large"
                    startIcon={<ContentCopy />}
                    onClick={handleShareProfile}
                    fullWidth
                  >
                    {shareCopied ? 'Link Copied' : 'Share Profile'}
                  </Button>
                  {savedHook.isClient && (
                    <Button
                      variant="outlined"
                      size="large"
                      color={savedHook.isSaved(contractorId) ? 'error' : 'primary'}
                      onClick={() => savedHook.toggle(contractorId)}
                      fullWidth
                    >
                      {savedHook.isSaved(contractorId) ? 'Saved ♥ — remove' : 'Save for later'}
                    </Button>
                  )}
                </>
              )}
            </Box>
          </Box>
        </CardContent>
      </Card>

      {/* Trust Score Card */}
      <Card sx={{ mb: 3 }}>
        <CardContent>
          <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 2 }}>
            <Box sx={{ display: 'flex', alignItems: 'center', gap: 2 }}>
              <Box>
                <Typography variant="body2" color="text.secondary" gutterBottom>
                  Trust Score
                </Typography>
                {trustScoreProvisional ? (
                  <Chip label="New on Eqaya" color="info" sx={{ fontWeight: 600 }} />
                ) : (
                  <Typography variant="h3" sx={{ fontWeight: 700, color: trustScore >= 80 ? 'success.main' : trustScore >= 60 ? 'warning.main' : 'error.main' }}>
                    {trustScore}%
                  </Typography>
                )}
              </Box>
              <Box>
                {!trustScoreProvisional && (
                  <LinearProgress
                    variant="determinate"
                    value={trustScore}
                    sx={{
                      width: 200,
                      height: 10,
                      borderRadius: 5,
                      bgcolor: 'grey.200',
                      '& .MuiLinearProgress-bar': {
                        bgcolor: trustScore >= 80 ? 'success.main' : trustScore >= 60 ? 'warning.main' : 'error.main'
                      }
                    }}
                  />
                )}
                <Typography variant="caption" color="text.secondary" sx={{ mt: 0.5, display: 'block' }}>
                  {trustScoreProvisional
                    ? 'Not enough completed work or reviews yet to rate this contractor'
                    : `${trustScore >= 80 ? 'Excellent' : trustScore >= 60 ? 'Good' : 'Fair'} reliability rating`}
                </Typography>
              </Box>
            </Box>

            <IconButton onClick={() => setTrustScoreExpanded(!trustScoreExpanded)}>
              {trustScoreExpanded ? <ExpandLess /> : <ExpandMore />}
            </IconButton>
          </Box>

          <Collapse in={trustScoreExpanded}>
            <Divider sx={{ my: 2 }} />
            <Typography variant="subtitle2" gutterBottom sx={{ fontWeight: 600 }}>
              Trust Score Breakdown
            </Typography>
            <Typography variant="caption" color="text.secondary" paragraph>
              Our Trust Score is calculated from multiple performance indicators to give you confidence in your hiring decision.
            </Typography>

            <Grid container spacing={2}>
              <Grid item xs={12} sm={6} md={4}>
                <Paper sx={{ p: 2, bgcolor: 'grey.50' }}>
                  <Typography variant="caption" color="text.secondary">
                    Client Rating (30%)
                  </Typography>
                  <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mt: 1 }}>
                    <Star sx={{ color: 'warning.main' }} />
                    <Typography variant="h6" sx={{ fontWeight: 600 }}>
                      {trustScoreBreakdown.ratingScore || Math.round(rating * 20)}%
                    </Typography>
                  </Box>
                  <Typography variant="caption" color="text.secondary">
                    Based on {reviewCount} reviews
                  </Typography>
                </Paper>
              </Grid>

              <Grid item xs={12} sm={6} md={4}>
                <Paper sx={{ p: 2, bgcolor: 'grey.50' }}>
                  <Typography variant="caption" color="text.secondary">
                    On-Time Delivery (25%)
                  </Typography>
                  <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mt: 1 }}>
                    <Schedule sx={{ color: 'success.main' }} />
                    <Typography variant="h6" sx={{ fontWeight: 600 }}>
                      {trustScoreBreakdown.onTimeScore || onTimeRate}%
                    </Typography>
                  </Box>
                  <Typography variant="caption" color="text.secondary">
                    Projects completed on time
                  </Typography>
                </Paper>
              </Grid>

              <Grid item xs={12} sm={6} md={4}>
                <Paper sx={{ p: 2, bgcolor: 'grey.50' }}>
                  <Typography variant="caption" color="text.secondary">
                    Response Rate (20%)
                  </Typography>
                  <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mt: 1 }}>
                    <Message sx={{ color: 'info.main' }} />
                    <Typography variant="h6" sx={{ fontWeight: 600 }}>
                      {trustScoreBreakdown.responseScore || responseRate}%
                    </Typography>
                  </Box>
                  <Typography variant="caption" color="text.secondary">
                    Quick communication
                  </Typography>
                </Paper>
              </Grid>

              <Grid item xs={12} sm={6} md={4}>
                <Paper sx={{ p: 2, bgcolor: 'grey.50' }}>
                  <Typography variant="caption" color="text.secondary">
                    Dispute Resolution (15%)
                  </Typography>
                  <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mt: 1 }}>
                    <CheckCircle sx={{ color: 'success.main' }} />
                    <Typography variant="h6" sx={{ fontWeight: 600 }}>
                      {trustScoreBreakdown.disputeScore || 100}%
                    </Typography>
                  </Box>
                  <Typography variant="caption" color="text.secondary">
                    Issues resolved successfully
                  </Typography>
                </Paper>
              </Grid>

              <Grid item xs={12} sm={6} md={4}>
                <Paper sx={{ p: 2, bgcolor: 'grey.50' }}>
                  <Typography variant="caption" color="text.secondary">
                    Verification (10%)
                  </Typography>
                  <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mt: 1 }}>
                    <Verified sx={{ color: verified ? 'primary.main' : 'grey.400' }} />
                    <Typography variant="h6" sx={{ fontWeight: 600 }}>
                      {trustScoreBreakdown.verificationScore || (verified ? 100 : 70)}%
                    </Typography>
                  </Box>
                  <Typography variant="caption" color="text.secondary">
                    {verified ? 'Documents verified' : 'Basic verification'}
                  </Typography>
                </Paper>
              </Grid>

              <Grid item xs={12} sm={6} md={4}>
                <Paper sx={{ p: 2, bgcolor: 'grey.50' }}>
                  <Typography variant="caption" color="text.secondary">
                    Total Projects
                  </Typography>
                  <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mt: 1 }}>
                    <Work sx={{ color: 'primary.main' }} />
                    <Typography variant="h6" sx={{ fontWeight: 600 }}>
                      {completedProjects}
                    </Typography>
                  </Box>
                  <Typography variant="caption" color="text.secondary">
                    Successfully completed
                  </Typography>
                </Paper>
              </Grid>
            </Grid>
          </Collapse>
        </CardContent>
      </Card>

      {/* Tabs Section */}
      <Card>
        <Tabs value={activeTab} onChange={(e, val) => setActiveTab(val)} sx={{ borderBottom: 1, borderColor: 'divider' }}>
          <Tab label="About" />
          <Tab label="Portfolio" />
          <Tab label="Reviews" />
        </Tabs>

        <CardContent sx={{ minHeight: 300 }}>
          {/* About Tab */}
          {activeTab === 0 && (
            <Box>
              <Typography variant="h6" gutterBottom sx={{ fontWeight: 600 }}>
                About
              </Typography>
              <Typography variant="body1" paragraph>
                {bio}
              </Typography>

              {specializations.length > 0 && (
                <>
                  <Typography variant="h6" gutterBottom sx={{ fontWeight: 600, mt: 3 }}>
                    Specializations
                  </Typography>
                  <Box sx={{ display: 'flex', gap: 1, flexWrap: 'wrap' }}>
                    {specializations.map((spec, index) => (
                      <Chip key={index} label={spec} />
                    ))}
                  </Box>
                </>
              )}

              {/* Certifications */}
              {Array.isArray(contractor.certifications) && contractor.certifications.length > 0 && (
                <>
                  <Typography variant="h6" gutterBottom sx={{ fontWeight: 600, mt: 3 }}>
                    Certifications
                  </Typography>
                  <List dense>
                    {contractor.certifications.map((cert, index) => (
                      <ListItem key={cert.id || index}>
                        <CheckCircle sx={{ mr: 2, color: 'success.main', fontSize: 18 }} />
                        <ListItemText
                          primary={typeof cert === 'string' ? cert : cert.name}
                          secondary={typeof cert === 'object' ? [cert.issuer, cert.date].filter(Boolean).join(' · ') : null}
                        />
                      </ListItem>
                    ))}
                  </List>
                </>
              )}

              <Typography variant="h6" gutterBottom sx={{ fontWeight: 600, mt: 3 }}>
                Contact
              </Typography>
              <Button
                variant="outlined"
                startIcon={<Message />}
                onClick={handleSendMessage}
                disabled={isPreview}
                fullWidth
                sx={{ mb: 1 }}
              >
                {isPreview ? 'Message button shown to owners' : 'Send Message'}
              </Button>
            </Box>
          )}

          {/* Portfolio Tab */}
          {activeTab === 1 && (
            <Box>
              <Typography variant="h6" gutterBottom sx={{ fontWeight: 600 }}>
                Portfolio
              </Typography>
              
              {portfolioItems.length > 0 ? (
                <Grid container spacing={2}>
                  {portfolioItems.map((item) => {
                    const images = normalizeImageList(item.after_images);
                    const mainImage = images[0] || item.thumbnail_url || null;

                    return (
                      <Grid item xs={12} sm={6} md={4} key={item.id}>
                        <Card
                          onClick={() => setSelectedPortfolioItem(item)}
                          sx={{
                            height: '100%',
                            cursor: 'pointer',
                            transition: 'box-shadow 0.2s ease, transform 0.2s ease',
                            '&:hover': {
                              boxShadow: 4,
                              transform: 'translateY(-2px)'
                            }
                          }}
                        >
                          {mainImage && (
                            <Box
                              component="img"
                              src={mainImage}
                              alt={item.project_title}
                              sx={{
                                width: '100%',
                                height: 200,
                                objectFit: 'cover'
                              }}
                            />
                          )}
                          <CardContent>
                            <Typography variant="h6" gutterBottom>
                              {item.project_title || 'Project'}
                            </Typography>
                            <Typography variant="body2" color="text.secondary" paragraph>
                              {item.description || 'No description available'}
                            </Typography>
                            {item.project_value && (
                              <Typography variant="body2" color="primary" sx={{ fontWeight: 600 }}>
                                {item.currency || 'USD'} {parseFloat(item.project_value).toLocaleString()}
                              </Typography>
                            )}
                            <Typography variant="caption" color="text.secondary" sx={{ display: 'block', mt: 1 }}>
                              View project photos
                            </Typography>
                          </CardContent>
                        </Card>
                      </Grid>
                    );
                  })}
                </Grid>
              ) : (
                <Alert severity="info">
                  This contractor hasn't added any portfolio items yet.
                </Alert>
              )}
            </Box>
          )}

          {/* Reviews Tab */}
          {activeTab === 2 && (
            <Box>
              <Typography variant="h6" gutterBottom sx={{ fontWeight: 600 }}>
                Client Reviews
              </Typography>
              
              {reviews.length > 0 ? (
                <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2 }}>
                  {reviews.map((review) => (
                    <Paper key={review.id} sx={{ p: 2 }}>
                      <Box sx={{ display: 'flex', justifyContent: 'space-between', mb: 1 }}>
                        <Box>
                          <Typography variant="subtitle2" sx={{ fontWeight: 600 }}>
                            {review.reviewer_name || review.reviewerName || 'Anonymous'}
                          </Typography>
                          <Rating value={review.rating} readOnly size="small" />
                        </Box>
                        <Typography variant="caption" color="text.secondary">
                          {new Date(review.created_at || review.createdAt).toLocaleDateString()}
                        </Typography>
                      </Box>
                      {review.title && (
                        <Typography variant="subtitle2" gutterBottom>
                          {review.title}
                        </Typography>
                      )}
                      <Typography variant="body2" color="text.secondary">
                        {review.review_text || review.reviewText || 'No review text provided'}
                      </Typography>
                    </Paper>
                  ))}
                </Box>
              ) : (
                <Alert severity="info">
                  No reviews yet. Be the first to work with this contractor and leave a review!
                </Alert>
              )}
            </Box>
          )}
        </CardContent>
      </Card>

      {/* AI Quote Request Dialog */}
      <AIContractorHiringDialog
        open={quoteDialogOpen && !isPreview}
        onClose={() => setQuoteDialogOpen(false)}
        onComplete={(data) => {
          console.log('Quote requests sent:', data);
          setQuoteDialogOpen(false);
        }}
        contractorId={contractor?.id || contractor?.user_id}
      />

      <Dialog
        open={Boolean(selectedPortfolioItem)}
        onClose={() => setSelectedPortfolioItem(null)}
        maxWidth="md"
        fullWidth
      >
        <DialogTitle sx={{ pr: 6 }}>
          {selectedPortfolioItem?.project_title || 'Portfolio Project'}
          <IconButton
            aria-label="Close portfolio project"
            onClick={() => setSelectedPortfolioItem(null)}
            sx={{ position: 'absolute', right: 12, top: 12 }}
          >
            <Close />
          </IconButton>
        </DialogTitle>
        <DialogContent dividers>
          {selectedPortfolioImages.length > 0 && (
            <Grid container spacing={2} sx={{ mb: 2 }}>
              {selectedPortfolioImages.map((image, index) => (
                <Grid item xs={12} sm={6} key={`${image}-${index}`}>
                  <Box
                    component="img"
                    src={image}
                    alt={`${selectedPortfolioItem?.project_title || 'Portfolio project'} ${index + 1}`}
                    sx={{
                      width: '100%',
                      height: 280,
                      objectFit: 'cover',
                      borderRadius: 1,
                      border: '1px solid',
                      borderColor: 'divider'
                    }}
                  />
                </Grid>
              ))}
            </Grid>
          )}
          <Typography variant="body1" color="text.secondary">
            {selectedPortfolioItem?.description || 'No description available'}
          </Typography>
        </DialogContent>
      </Dialog>
    </Container>
  );
};

export default EnhancedContractorProfile;
